import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, CheckCircle2, Quote, TrendingUp } from "lucide-react";
import { useCaseStudy } from "@/data/hooks";
import Seo, { makeBreadcrumb } from "@/components/seo/Seo";
import RelatedContentCarousel from "@/components/RelatedContentCarousel";

// Full long-form content for individual case studies (keyed by slug).
// These are the build-time fallback used when MongoDB hasn't yet been seeded
// or the API is unreachable. The same fields are also stored in the
// `case_studies` collection so the Admin CMS can edit them.
const details = {
  logotology: {
    subtitle:
      "Monica Scott on turning fragmented spreadsheets and physical folders into a unified production command center.",
    heroImage: "/api/media/a9941a96-ead7-56ad-a18b-ed6a8936ca41",
    facts: [
      { label: "Industry", value: "Multi-brand Promotional Products & Apparel" },
      { label: "Brands Operated", value: "Logotology, GolfTournament.com, Classroom Apparel" },
      { label: "Product Used", value: "Antera Advance" },
      { label: "Integrations", value: "QuickBooks Online, Fiserv CardConnect" },
    ],
    quote: {
      text:
        "Before Advance, our daily operations felt less like 'management' and more like data archaeology - digging through multiple systems to track down clients and orders. Now, housing all my orders in one place has essentially turned Advance into our operational command center. It took us from reactive firefighting to proactive planning. The biggest change? We actually spend our day acting on our orders rather than just trying to locate them.",
      author: "Monica Scott",
      role: "Owner, Logotology",
      avatar: "/api/media/13f39edc-658e-529d-b412-c02176503530",
    },
    challenge: [
      "Rapid scaling and the acquisition of new ventures (GolfTournament.com, Classroom Apparel) outpaced the existing infrastructure.",
      "Sales activity lived in one system, accounting in another, and production relied on spreadsheets, whiteboards, and physical folders.",
      "Leadership acted as a manual gatekeeper - constantly verifying payments before orders could move to production.",
      "Misleading accounting records (negative receivables) and the lack of real-time data made daily output unpredictable.",
      "Staff manually chased down departments for artwork approval, goods received, and payment status - every order felt like a daily gamble.",
    ],
    solution: [
      "Migrated all order intake, production routing, and fulfillment onto Antera's Advance platform - one unified workflow end to end.",
      "Used Advance's Corporate Identity feature to route orders from multiple brands into a single, centralized production queue without losing brand-level reporting.",
      "Automated financial gatekeeping by integrating QuickBooks Online and Fiserv CardConnect for real-time payment verification.",
      "Replaced physical folders with a fully digital, visual production workflow - order details, artwork, thread colors, and production requirements visible directly to the floor team.",
    ],
    results: [
      { metric: "+40%", label: "Manufacturing output with the same equipment and headcount" },
      { metric: "+30%", label: "Sustained year-over-year business growth" },
      { metric: "-50%", label: "Reduction in time from order receipt to active production" },
      { metric: "-20%", label: "Reduction in accounting workload - manual reconciliation eliminated" },
      { metric: "+10%", label: "Increase in client reorder rates from faster fulfillment" },
    ],
  },

  "beachley-medical": {
    subtitle:
      "How a national physical therapy corporation rolled out branded employee + corporate-funded apparel across 900+ locations without overwhelming managers.",
    heroImage: "/api/media/b99cd589-ddd4-45e6-816e-9c79562c9ab1",
    facts: [
      { label: "Industry", value: "Orthopedic Rehab / National Physical Therapy" },
      { label: "Scale", value: "900+ locations · 9,000 employees" },
      { label: "Product Used", value: "Antera Aether + Advance" },
      { label: "Integrations", value: "QuickBooks · Warehouse 2-way sync" },
    ],
    quote: {
      text:
        "The system will NOT allow that user to go over the budget. Antera lets the client control spending without the friction of a manual approval process - and it keeps every store on-brand across every location.",
      author: "Beachley Medical Team",
      role: "Account Lead",
      avatar: "",
    },
    challenge: [
      "Reporting fragmentation: difficulty distinguishing between corporate-funded spend and employee-funded personal purchases.",
      "Approval bottlenecks: a desire to decentralize ordering without overwhelming managers with manual approval tasks.",
      "National brand stewardship: ensuring ad-hoc orders at the local level adhered to strict corporate visual identity.",
    ],
    solution: [
      "Deployed a dual-storefront model on Antera Aether Web Stores - a Teamstore, an Apparel Store, and Pop-Up Campaigns - each tuned for a different buyer type.",
      "Used Antera Advance as the back-office system of record, enforcing brand-approved SKUs at order intake.",
      "Activated the Budget / Bank feature for automated spend governance with a hard-stop threshold - no manual approval friction.",
      "Set up a two-way data integration with the warehouse for live stock levels and automated notifications when items shipped.",
      "Integrated QuickBooks for real-time financial visibility and bulk invoicing mapped to a three-part cost-code structure.",
    ],
    results: [
      { metric: "900+", label: "Locations supplied from a single rollout" },
      { metric: "9,000", label: "Employees ordering on-brand apparel" },
      { metric: "100%", label: "Brand consistency maintained across the national footprint" },
      { metric: "0", label: "Manual approvals required for in-budget orders" },
    ],
  },
};

export default function CaseStudyDetailPage() {
  const { slug } = useParams();
  const { caseStudy: study, loading } = useCaseStudy(slug);
  // If the Mongo doc has rich long-form fields (subtitle, hero_image, facts, etc.) use those.
  // Otherwise fall back to the hardcoded `details` map for legacy slugs.
  const detail = (study && (study.subtitle || (study.facts && study.facts.length) || study.quote?.text))
    ? {
        subtitle: study.subtitle || "",
        heroImage: study.heroImage || study.image,
        facts: study.facts || [],
        quote: study.quote || { text: "", author: "", role: "", avatar: "" },
        challenge: study.challenge || [],
        solution: study.solution || [],
        results: study.results || [],
      }
    : details[slug];

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-20">
        <p className="text-slate-400">Loading case study…</p>
      </div>
    );
  }

  if (!study || !detail) {
    return (
      <div data-testid="case-study-not-found" className="min-h-[60vh] flex items-center justify-center text-center px-4 pt-20">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Case study not found</h1>
          <p className="mt-2 text-slate-500">This story isn't available yet.</p>
          <Link to="/case-studies" className="inline-block mt-6">
            <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Case Studies
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="case-study-detail-page" className="page-transition">
      <Seo
        title={`${study.headline} | ${study.company} Case Study | Antera`}
        description={detail.subtitle}
        keywords={`antera case study, ${study.industry.toLowerCase()} case study, promotional products software success story, ${study.company.toLowerCase()}`}
        path={`/case-studies/${study.slug}`}
        type="article"
        image={detail.heroImage}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            makeBreadcrumb([
              { name: "Home", path: "/" },
              { name: "Case Studies", path: "/case-studies" },
              { name: study.company, path: `/case-studies/${study.slug}` },
            ]),
            {
              "@type": "Article",
              headline: study.headline,
              description: detail.subtitle,
              image: detail.heroImage,
              author: { "@type": "Organization", name: "Antera Software" },
              publisher: {
                "@type": "Organization",
                name: "Antera Software",
                logo: {
                  "@type": "ImageObject",
                  url: "/api/media/adff924b-451a-45d0-a130-026ae966ce9a",
                },
              },
              about: {
                "@type": "Organization",
                name: study.company,
              },
            },
          ],
        }}
      />
      {/* Hero */}
      <section className="pt-28 lg:pt-36 pb-16 lg:pb-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/case-studies"
            data-testid="case-study-back"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-sky-600 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> All case studies
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6">
            <span className="text-xs font-semibold text-sky-600 uppercase tracking-widest">{study.industry}</span>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              {study.headline}
            </h1>
            <p className="mt-4 text-base lg:text-lg text-slate-500 max-w-3xl leading-relaxed">{detail.subtitle}</p>
          </motion.div>

          <div className="mt-10 rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
            <img src={detail.heroImage} alt={study.company} className="w-full h-72 sm:h-96 object-cover" />
          </div>
        </div>
      </section>

      {/* Facts strip */}
      <section className="py-8 bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {detail.facts.map((f) => (
            <div key={f.label}>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{f.label}</p>
              <p className="mt-1 text-sm font-semibold text-slate-800">{f.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Body content */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main story */}
          <div className="lg:col-span-2 space-y-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">The Challenge</h2>
              <ul className="mt-4 space-y-3">
                {detail.challenge.map((c) => (
                  <li key={c} className="flex gap-3 text-base text-slate-600 leading-relaxed">
                    <span className="text-sky-500 mt-2 shrink-0">•</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900">The Solution</h2>
              <ul className="mt-4 space-y-3">
                {detail.solution.map((s) => (
                  <li key={s} className="flex gap-3 text-base text-slate-600 leading-relaxed">
                    <CheckCircle2 className="h-5 w-5 text-sky-500 shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <blockquote className="relative bg-sky-50/60 border border-sky-100 rounded-2xl p-7 lg:p-8">
              <Quote className="absolute top-5 right-5 h-8 w-8 text-sky-200" />
              <p className="text-lg italic text-slate-700 leading-relaxed">"{detail.quote.text}"</p>
              <div className="mt-6 flex items-center gap-4">
                <img src={detail.quote.avatar} alt={detail.quote.author} className="w-12 h-12 rounded-full object-cover border-2 border-white shadow" />
                <div>
                  <p className="text-sm font-bold text-slate-900">{detail.quote.author}</p>
                  <p className="text-xs text-slate-500">{detail.quote.role}</p>
                </div>
              </div>
            </blockquote>
          </div>

          {/* Results sidebar */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 bg-slate-900 rounded-2xl p-7 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-sky-500/15 rounded-full blur-[60px]" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-xs font-semibold text-sky-400 uppercase tracking-widest mb-5">
                  <TrendingUp className="h-3.5 w-3.5" /> Results
                </div>
                <div className="space-y-5">
                  {detail.results.map((r) => (
                    <div key={r.label} className="pb-5 border-b border-slate-800 last:border-b-0 last:pb-0">
                      <p className="text-3xl font-extrabold text-white">{r.metric}</p>
                      <p className="text-xs text-slate-400 mt-1">{r.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 lg:py-20 bg-sky-600">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white">Ready to write your own story?</h2>
          <p className="mt-3 text-sky-100">See how Antera can power your team - from quote to invoice.</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-sky-700 hover:bg-sky-50 rounded-full px-8 h-12 text-sm font-semibold">
                Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/case-studies">
              <Button variant="outline" className="rounded-full px-8 h-12 text-sm font-semibold border-white/30 text-white hover:bg-sky-700 bg-transparent">
                More Stories
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <RelatedContentCarousel
        source="case-studies"
        title="More Customer Stories"
        subtitle="Hear from other businesses transforming their workflows with Antera."
        excludeSlug={slug}
        testId="case-study-related"
      />
    </div>
  );
}
